import { Reveal } from "./Reveal";
import { GALLERY_PAIRS } from "@/lib/gallery";

// Matches the "Before"/"After" chip already burnt into the supplied commercial photos.
const badgeCls =
  "absolute left-2 top-2 rounded bg-navy-deep/85 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white";

const GalleryCard = ({ pair, delay }) => {
  const ext = pair.ext || "png";
  const ratio = pair.ratio || "3 / 4";
  return (
    <Reveal delay={delay}>
      <figure
        className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"
        data-testid={`gallery-card-${pair.slug}`}
      >
        <div className="grid grid-cols-1 gap-1 bg-slate-200 sm:grid-cols-2">
          <div className="relative">
            <img
              src={`/gallery/${pair.slug}-before.${ext}`}
              alt={`Before cleaning — ${pair.alt}`}
              loading="lazy"
              decoding="async"
              style={{ aspectRatio: ratio }}
              className="w-full object-cover"
              data-testid={`gallery-before-${pair.slug}`}
            />
            {pair.label && <span className={badgeCls}>Before</span>}
          </div>
          <div className="relative">
            <img
              src={`/gallery/${pair.slug}-after.${ext}`}
              alt={`After cleaning by RD Cleaning Services — ${pair.alt}`}
              loading="lazy"
              decoding="async"
              style={{ aspectRatio: ratio }}
              className="w-full object-cover"
              data-testid={`gallery-after-${pair.slug}`}
            />
            {pair.label && <span className={badgeCls}>After</span>}
          </div>
        </div>
        <figcaption className="px-5 py-4 text-sm font-bold text-navy" data-testid={`gallery-caption-${pair.slug}`}>
          {pair.caption}
        </figcaption>
      </figure>
    </Reveal>
  );
};

export const ResultsGallery = ({ slugs }) => {
  const pairs = slugs ? slugs.map((s) => GALLERY_PAIRS.find((p) => p.slug === s)).filter(Boolean) : GALLERY_PAIRS;
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="results-gallery">
      {pairs.map((pair, i) => (
        <GalleryCard key={pair.slug} pair={pair} delay={(i % 3) * 0.08} />
      ))}
    </div>
  );
};

export const ResultsGallerySection = ({
  slugs,
  eyebrow = "Results",
  heading = "Before & After",
  text = "Each before image is shown beside its matching after image.",
  bg = "bg-slate-50",
  testid = "results-gallery-section",
}) => (
  <section className={`${bg} py-20 sm:py-28`} data-testid={testid}>
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-2xl">
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">{heading}</h2>
        <p className="mt-4 text-slate-600">{text}</p>
      </Reveal>
      <div className="mt-12">
        <ResultsGallery slugs={slugs} />
      </div>
    </div>
  </section>
);
